import {
  useMutation,
  type MutationState,
} from 'src/core/api/hooks/useMutation';

import { type AnalyticalFieldMapping } from '../../accounting';

export type AnalyticalFieldMappingCreate =
  | {
      codeType: 'customField';
      externalId: string;
      externalName: string;
      internalName: string;
      kind: 'mapped';
      fieldId: string | undefined;
    }
  | {
      codeType: 'costCenter';
      externalId: string;
      externalName: string;
      kind: 'mapped';
      fieldId: string | undefined;
    };

export type AnalyticalFieldMappingDelete = {
  externalId: string;
  kind: 'notMapped';
  fieldId: string | undefined;
};

export const useCreateAnalyticalFieldMappingMutation = (): MutationState<
  AnalyticalFieldMappingCreate[]
> => {
  return useMutation<AnalyticalFieldMappingCreate[]>({
    request: {
      type: 'rest',
      method: 'post',
      target: 'companyAPI',
      endpoint:
        '/accounting-integration/chart-of-accounts/analytical-fields-mappings',
    },
    options: {
      throwOnError: true,
      // Optimistically update the cache value on mutate
      onMutate: async ({ payload, client }): Promise<void> => {
        await client.cancelQueries('getAnalyticalFieldMappings');

        client.setQueryData<AnalyticalFieldMapping[]>(
          'getAnalyticalFieldMappings',
          (rawCachedMappings) => {
            let cachedMappings: AnalyticalFieldMapping[] =
              rawCachedMappings ?? [];

            for (const insert of payload) {
              cachedMappings = cachedMappings.map(
                (cachedMapping): AnalyticalFieldMapping => {
                  if (insert.externalId !== cachedMapping.externalId) {
                    return cachedMapping;
                  }

                  const baseCachedMapping = {
                    externalId: cachedMapping.externalId,
                    externalName: cachedMapping.externalName,
                    companyId: cachedMapping.companyId,
                    isArchived: cachedMapping.isArchived,
                  };

                  if (insert.codeType === 'customField') {
                    return {
                      ...baseCachedMapping,
                      internalName: insert.internalName,
                      kind: 'mapped',
                      codeType: 'customField',
                      internalId: '',
                      externalName: insert.externalName,
                      fieldId: insert.fieldId,
                    };
                  }

                  return {
                    ...baseCachedMapping,
                    codeType: 'costCenter',
                    kind: 'mapped',
                    fieldId: insert.fieldId,
                  };
                },
              );
            }

            return cachedMappings;
          },
        );
      },
      onSuccess({ client }): void {
        client.resetQueries(['getCustomFields']);
        client.resetQueries(['getAnalyticalFieldMappings']);
      },
    },
  });
};

export const useDeleteAnalyticalFieldMappingMutation = (): MutationState<
  AnalyticalFieldMappingDelete[]
> => {
  return useMutation<AnalyticalFieldMappingDelete[]>({
    request: {
      type: 'rest',
      method: 'put',
      target: 'companyAPI',
      endpoint:
        '/accounting-integration/chart-of-accounts/analytical-fields-mappings',
    },
    options: {
      throwOnError: true,
      // Optimistically update the cache value on mutate
      onMutate: async ({ payload, client }): Promise<void> => {
        await client.cancelQueries('getAnalyticalFieldMappings');

        client.setQueryData<AnalyticalFieldMapping[]>(
          'getAnalyticalFieldMappings',
          (rawCachedMappings) => {
            let cachedMappings: AnalyticalFieldMapping[] =
              rawCachedMappings ?? [];

            for (const update of payload) {
              cachedMappings = cachedMappings.map(
                (cachedMapping): AnalyticalFieldMapping => {
                  if (update.externalId !== cachedMapping.externalId) {
                    return cachedMapping;
                  }

                  const baseCachedMapping = {
                    externalId: cachedMapping.externalId,
                    externalName: cachedMapping.externalName,
                    companyId: cachedMapping.companyId,
                    isArchived: cachedMapping.isArchived,
                  };

                  return {
                    ...baseCachedMapping,
                    kind: 'notMapped',
                    fieldId: update.fieldId,
                  };
                },
              );
            }

            return cachedMappings;
          },
        );
      },
      onSuccess({ client }): void {
        client.invalidateQueries(['getAnalyticalFieldMappings']);
      },
    },
  });
};
